/**
 * Rep-range targets, keyed on the user's stated goal *and* on what kind of
 * exercise it is.
 *
 * `profiles.goal` is the `fitness_goal` enum (core.js:2352): strength,
 * hypertrophy, endurance, weight_loss, general. `fat_loss` is remapped to
 * `weight_loss` at the same chokepoint — this module never sees it.
 *
 * `exercises.exercise_type` is the second key: compound, isolation, cardio or
 * bodyweight (schema line 86; every seeded exercise carries one). Keying on the
 * goal alone was wrong in a way any lifter would spot instantly — it prescribed
 * a strength-goal user sets of 3 on cable crossovers and lateral raises. A
 * near-maximal triple belongs on a barbell squat; on an isolation movement it
 * buys nothing and loads a small muscle through a long lever. So accessory work
 * gets its own, higher table.
 *
 * Anything that is not a compound reads from the accessory table: isolation for
 * the obvious reason, cardio and bodyweight because both are rep-limited rather
 * than load-limited, and a 3-rep target on either is meaningless.
 */
const COMPOUND_RANGES = {
  strength: { low: 3, high: 6 },
  hypertrophy: { low: 8, high: 12 },
  endurance: { low: 12, high: 20 },
  weight_loss: { low: 10, high: 15 },
  general: { low: 8, high: 12 },
};

const ACCESSORY_RANGES = {
  strength: { low: 6, high: 10 },
  hypertrophy: { low: 10, high: 15 },
  endurance: { low: 15, high: 25 },
  weight_loss: { low: 12, high: 20 },
  general: { low: 10, high: 15 },
};

/**
 * @param {string} goal `profiles.goal`
 * @param {string} [exerciseType] `exercises.exercise_type`. Omitted (or unknown)
 *   is treated as a compound, matching the column's own default.
 */
export function getRepRange(goal, exerciseType) {
  const table = exerciseType && exerciseType !== 'compound' ? ACCESSORY_RANGES : COMPOUND_RANGES;
  return table[goal] || table.general;
}

/**
 * The rep target to come back at after a load cut. Deliberately the middle of
 * the range rather than its floor: a deload that drops the load *and* drops the
 * reps to the minimum cuts the session's stimulus twice over for one problem,
 * which is how the old engine turned every deload into a lost week.
 */
export function getRebuildReps({ low, high }) {
  return Math.round((low + high) / 2);
}
